import { ReactNode } from 'react'
import { Search } from 'lucide-react'

export type Column<T> = {
  key: keyof T | string
  label: string
  render?: (row: T) => ReactNode
  width?: string
}

type Props<T> = {
  columns: Column<T>[]
  data: T[]
  searchable?: boolean
  searchPlaceholder?: string
  searchValue?: string
  onSearchChange?: (value: string) => void
  actions?: ReactNode
  currentPage?: number
  totalItems?: number
  itemsPerPage?: number
  onPageChange?: (page: number) => void
  loading?: boolean
  emptyMessage?: string
}

export function AdminTable<T extends { key: string | number }>({
  columns,
  data,
  searchable = true,
  searchPlaceholder = 'Search...',
  searchValue,
  onSearchChange,
  actions,
  currentPage = 1,
  totalItems = 0,
  itemsPerPage = 10,
  onPageChange,
  loading = false,
  emptyMessage = 'No records found.',
}: Props<T>) {
  const totalPages = Math.ceil(totalItems / itemsPerPage)

  const getPageNumbers = () => {
    const pages: (number | string)[] = []
    
    if (totalPages <= 7) {
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i)
      }
    } else {
      pages.push(1)
      
      if (currentPage > 3) {
        pages.push('...')
      }
      
      const start = Math.max(2, currentPage - 1)
      const end = Math.min(totalPages - 1, currentPage + 1)
      
      for (let i = start; i <= end; i++) {
        pages.push(i)
      }
      
      if (currentPage < totalPages - 2) {
        pages.push('...')
      }
      
      pages.push(totalPages)
    }
    
    return pages
  }
  return (
    <div className='rounded-2xl bg-white border border-gray-100 shadow-sm'>
      <div className='flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 px-4 py-3 border-b border-gray-100'>
        {searchable ? (
          <div className='flex items-center gap-2 rounded-lg border border-gray-200 px-3 py-2 text-sm text-gray-600 bg-white w-full sm:w-auto'>
            <Search className='h-4 w-4 text-gray-400 flex-shrink-0' />
            <input
              placeholder={searchPlaceholder}
              value={onSearchChange ? (searchValue ?? '') : undefined}
              onChange={e => onSearchChange?.(e.target.value)}
              className='bg-transparent outline-none placeholder:text-gray-400 text-sm w-full min-w-0'
            />
          </div>
        ) : (
          <div className='hidden sm:block' />
        )}
        {actions && (
          <div className='flex items-center gap-2 flex-wrap sm:flex-nowrap'>
            {actions}
          </div>
        )}
      </div>
      <div className='overflow-x-auto -webkit-overflow-scrolling-touch'>
        <table className='min-w-full text-sm text-gray-700'>
          <thead className='bg-gray-50 text-left text-xs font-semibold text-gray-500 text-start'>
            <tr>
              {columns.map(col => (
                <th key={col.key as string} className='px-4 py-3 text-start whitespace-nowrap' style={col.width ? { width: col.width } : undefined}>
                  {col.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              Array.from({ length: Math.max(3, Math.min(itemsPerPage, 5)) }).map((_, rowIdx) => (
                <tr key={`skeleton-${rowIdx}`} className='border-t border-gray-100'>
                  {columns.map((_col, colIdx) => (
                    <td key={`skeleton-${rowIdx}-${colIdx}`} className='px-4 py-3'>
                      <div className='h-3 rounded bg-gray-200 animate-pulse' style={{ width: `${60 + ((rowIdx + colIdx) % 4) * 10}%` }} />
                    </td>
                  ))}
                </tr>
              ))
            ) : data.length === 0 ? (
              <tr>
                <td colSpan={columns.length} className='px-4 py-10 text-center text-sm text-gray-500'>
                  {emptyMessage}
                </td>
              </tr>
            ) : (
              data.map(row => (
                <tr key={row.key} className='border-t border-gray-100 hover:bg-gray-50 transition text-start'>
                  {columns.map(col => (
                    <td key={`${row.key}-${col.key as string}`} className='px-4 py-3 text-start whitespace-nowrap'>
                      {col.render ? col.render(row) : (row as any)[col.key]}
                    </td>
                  ))}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
      {totalPages > 1 && (
        <div className='flex items-center justify-center sm:justify-end gap-2 px-4 py-3 border-t border-gray-100 text-sm text-gray-600 overflow-x-auto'>
          {getPageNumbers().map((page, idx) => (
            page === '...' ? (
              <span key={`ellipsis-${idx}`} className='px-3 py-1 rounded-full border border-gray-200 text-gray-800 flex-shrink-0'>
                …
              </span>
            ) : (
              <button
                key={page}
                onClick={() => onPageChange?.(page as number)}
                className={`px-3 py-1 rounded-full border transition-colors flex-shrink-0 ${
                  page === currentPage
                    ? 'bg-[#ff4f3c] text-white border-[#ff4f3c]'
                    : 'border-gray-200 text-gray-800 hover:bg-gray-50'
                }`}
              >
                {page}
              </button>
            )
          ))}
        </div>
      )}
    </div>
  )
}
